/**
 * The registry's type switcher (#100): capabilities, initiatives, programs,
 * milestones, objectives, themes, ideas, requirements — one table, columns from attribute
 * bindings, `?type=<code>` in the URL. This is a FIXED, ordered allowlist of entity_type codes
 * (the tab order the design mock shows) — department/domain live in Admin, framework_node and
 * section are reference data with no list of their own, user/request/document/
 * strategic_imperative/regulation already have their own screens. Every LABEL still comes from
 * the schema (`GET /api/schema`), never a literal — only the set and order of codes is fixed.
 */
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { endpoints } from '@/api/endpoints';

export const REGISTRY_TYPES = [
  'capability',
  'initiative',
  // #747: the retired product type ("Legacy apps") has no registry tab; its records stay readable at /registry/product/<key>
  'program',
  'milestone',
  'objective',
  'theme',
  'idea',
  'requirement',
  // G21 (#192): systems list and open like any other registry type.
  'system',
] as const;

export type RegistryTypeCode = (typeof REGISTRY_TYPES)[number];

export interface RegistryEntityType {
  code: string;
  label: string;
  plural: string;
}

export function isRegistryType(code: string): code is RegistryTypeCode {
  return (REGISTRY_TYPES as readonly string[]).includes(code);
}

/**
 * R-15 C (T-078, T-072): the switcher's tabs are the registry types the app on screen owns (GET /api/apps
 * `entityTypes`, from its module declaration): CIGS lists capabilities and its planning types; system (a platform
 * type, reached through the Systems lens), ideas (Request a change) and requirements (their request or investment)
 * leave it. Before the apps answer, the fixed order stands.
 */
export function switcherTypes<T extends string>(order: readonly T[], appTypes: readonly string[] | null | undefined): T[] {
  if (!appTypes?.length) return [...order];
  return order.filter((t) => appTypes.includes(t));
}

/**
 * R-15 T-121: the type `?type=` opens — a tab, or any type the schema knows (taken on trust while the
 * schema list loads); anything else opens the default.
 */
export function registryTypeFor(asked: string, schema: { pending: boolean; codes: readonly string[] | undefined }, fallback: string): string {
  if (isRegistryType(asked)) return asked;
  return asked !== '' && (schema.pending || Boolean(schema.codes?.includes(asked))) ? asked : fallback;
}

interface SchemaTypes {
  tabs: RegistryEntityType[];
  /** R-15 T-121: every entity type the schema knows, so an expert can open any type's records by `?type=` */
  all: RegistryEntityType[];
}

const toType = (t: Record<string, unknown> | undefined, code: string): RegistryEntityType => ({
  code,
  label: String(t?.label ?? code),
  plural: String(t?.plural ?? `${code}s`),
});

function useSchemaTypes<R>(select: (d: SchemaTypes) => R) {
  return useQuery<SchemaTypes, Error, R>({
    queryKey: ['cigs', 'entity-types'],
    staleTime: 5 * 60_000,
    select,
    queryFn: async () => {
      const raw = await apiFetch<Record<string, unknown>>(endpoints.schema);
      const list = ((raw.entityTypes as Record<string, unknown>[] | undefined) ?? (raw.entity_types as Record<string, unknown>[] | undefined) ?? []) as Record<
        string,
        unknown
      >[];
      const byCode = new Map(list.map((t) => [String(t.code), t]));
      return { tabs: REGISTRY_TYPES.map((code) => toType(byCode.get(code), code)), all: list.map((t) => toType(t, String(t.code))) };
    },
  });
}

/** The switcher's tabs, in the fixed order. */
export function useEntityTypes() {
  return useSchemaTypes((d) => d.tabs);
}

/** Every entity type the schema knows (the switcher's tabs are a subset). */
export function useAllEntityTypes() {
  return useSchemaTypes((d) => d.all);
}
